/**
 * `/blog/:article` — renders the knowledge-base article for the slug and locale, or the site's
 * 404.
 *
 * The legacy Strapi export is done and Strapi is no longer a fallback: the server has served
 * knowledge-base articles only since 2026-09-18 (`loadBlogArticle` in `marketingSsrLoaders.ts`),
 * and a slug the knowledge base does not hold gets a real 404 from `marketingSsrHandler`. This
 * component makes the client say the same thing, so a hard load and a client-side navigation to
 * one URL can no longer disagree (they did while a miss here rendered the Strapi `BlogArticle`).
 *
 * On a hard load the server already resolved the article and hands it over as `initialData`. On
 * client-side navigation there is no SSR payload, so this asks for the article by slug AND
 * LOCALE. The locale is not optional — the resolver localises the document it returns, so asking
 * without one returns the English article. Resolving here rather than inside `DiscoverArticle`
 * keeps the 404 decision in one place: a component that renders null on a miss cannot delegate to
 * another one.
 *
 * Not found is the same `NotFound` page the marketing site shows for any unknown route. That
 * includes a failed request: there is no error or retry component on the website, so a failure
 * renders the 404 too and is logged, which is the only trace of it.
 */

import type { ArticleData } from "components/discover/articleData";
import { useEffect, useState } from "react";
import DiscoverArticle from "./DiscoverArticle";
import ItineraryArticle from "./ItineraryArticle";
import NotFound from "./NotFound";

interface BlogArticleRouteProps {
	initialData?: { article?: any; localeSlugs?: Record<string, string>; source?: "kb" | "strapi" };
	match?: { params: { article?: string } };
	/**
	 * The URL locale, passed to every route component by `MarketingShell.tsx:91` and, on the SPA's own
	 * route table, by `WebsiteRoutes.tsx`.
	 *
	 * It has to be DECLARED here, not just read: these wrappers destructure their props, and a
	 * prop the interface does not name is silently dropped — the same failure
	 * `guideRoutesLang.test.tsx` was written for after it happened twice on the guide pages.
	 * Without it the client-side query below asked for an article with no language and got
	 * English, so clicking into /pt/blog/where-to-stay-in-bangkok from the index rendered the
	 * English article under a Portuguese URL while a hard load of the same URL was correct.
	 */
	lang?: string;
}

/**
 * `for` is the slug+locale this resolution was fetched for, and it is what makes client-side
 * navigation work. Without it the effect below guards on `state !== "loading"` alone, which is
 * true for the whole life of the first article: navigating from one article to another changed
 * the URL and re-ran the effect, the guard returned immediately, and the previous article stayed
 * on screen under the new address (123yb6937h1's sibling — reported on /blog/bangkok-rooftop-bars
 * -> /blog/where-to-stay-in-bangkok). Only the links that do a full page load escaped it, which
 * is why `ReadMore` looked fine and the related cards did not.
 */
type ResolvedFor = { slug?: string; lang?: string };
type Resolution =
	| { state: "loading" }
	| { state: "kb"; for: ResolvedFor; article: ArticleData; localeSlugs?: Record<string, string> }
	| { state: "notFound"; for: ResolvedFor };

const BlogArticleRoute = ({ initialData, match, lang }: BlogArticleRouteProps) => {
	const slug = match?.params?.article;
	const [resolved, setResolved] = useState<Resolution>(() => {
		if (initialData?.source === "kb" && initialData.article)
			return { state: "kb", for: { slug, lang }, article: initialData.article, localeSlugs: initialData.localeSlugs };
		// Anything else the server handed over is not a knowledge-base article: `source: "strapi"` (the
		// server stopped sending it on 2026-09-18, so only a stale cache entry could) or an article with
		// no `source`. There is no effect during SSR to recover it, so a `loading` here would render the
		// whole page as an empty string; settle on the 404 instead.
		if (initialData) return { state: "notFound", for: { slug, lang } };
		return { state: "loading" };
	});

	useEffect(() => {
		if (!slug) return undefined;
		// Re-arm for a different article. `state !== "loading"` on its own is true for as long as
		// the first article is mounted, so a client-side navigation would fall straight through
		// this guard and leave the previous article rendered under the new URL.
		if (resolved.state !== "loading") {
			if (resolved.for.slug === slug && resolved.for.lang === lang) return undefined;
			setResolved({ state: "loading" });
			return undefined;
		}
		let cancelled = false;
		fetch("/graphql", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				// `$language` matters as much as `$slug`. The resolver localises through
				// `localizeExploreDoc`, so omitting it does not fail — it quietly returns English.
				query: "query BlogArticle($slug: String!, $language: String) { blogArticle(slug: $slug, language: $language) }",
				variables: { slug, language: lang },
			}),
		})
			.then((r) => r.json())
			.then((r) => {
				if (cancelled) return;
				const article = r?.data?.blogArticle;
				// A resolver failure is a 200 with `errors` and no data. That is a failed request, not an
				// article the knowledge base does not hold, so it goes to the `.catch` and gets logged.
				if (!article && r?.errors?.length) throw new Error(r.errors[0]?.message || "GraphQL error");
				// 123yb692puz / 123yb692pwf: `localeSlugs` rides on the same payload as the article
				// (the `blogArticle` resolver now attaches it — see explore.ts), not a separate field
				// on the response. Pulling it off here is what makes the client-side navigation path
				// carry the language switcher instead of silently dropping it, the way the SSR path
				// already does via `initialData.localeSlugs` above.
				const { localeSlugs, ...rest } = article || {};
				setResolved(article ? { state: "kb", for: { slug, lang }, article: rest, localeSlugs } : { state: "notFound", for: { slug, lang } });
			})
			.catch((error) => {
				if (cancelled) return;
				console.error("[BlogArticleRoute] blogArticle request failed", { slug, error });
				setResolved({ state: "notFound", for: { slug, lang } });
			});
		return () => {
			cancelled = true;
		};
	}, [slug, resolved.state, lang]);

	if (resolved.state === "kb") {
		// `articleType === "itinerary"` is the discriminator — see the field's doc comment in
		// `articleData.ts` for the full case against the alternatives. It used to be a non-empty
		// `stops` array, which looked reasonable but wasn't: `itinerary_stops()` in the exporter
		// falls back to collecting every section that carries a `geo`, and a listicle picks that
		// up the moment one of its sections links out to a `/discover/*` hub. That fallback put
		// ~95% of the 1084-article corpus (all but the 63 real itineraries) through the route
		// layout — stop-by-stop chrome and a route sidebar on plain guide prose.
		// `ItineraryArticle` reads the resolved `article` — its sections, stops, byline and
		// breadcrumbs — instead of the hardcoded Japan fixture that once made every article in
		// the category serve "Two weeks in Japan" (123yb691t00).
		if (resolved.article.articleType === "itinerary") return <ItineraryArticle article={resolved.article} localeSlugs={resolved.localeSlugs} />;
		return <DiscoverArticle article={resolved.article} localeSlugs={resolved.localeSlugs} />;
	}
	if (resolved.state === "notFound") return <NotFound lang={lang} />;
	return null;
};

export default BlogArticleRoute;
